/**
 * CMS content placeholders — replace {{key}} in CMS body (and nav HTML) with values from Settings & Branding.
 *
 * Use in CMS page body or navigation HTML, e.g.:
 *   Call us at {{phone-number}}
 *   Email: {{contact-email}}
 *   {{company-address}}
 *   {{brand-name}} — {{brand-tagline}}
 *
 * Placeholders are case-sensitive. Unknown keys are replaced with empty string.
 */

export const CMS_PLACEHOLDER_KEYS = [
  'phone-number',
  'contact-email',
  'company-address',
  'brand-name',
  'brand-tagline',
] as const;

export type CMSPlaceholderKey = (typeof CMS_PLACEHOLDER_KEYS)[number];

/** Map placeholder key (e.g. phone-number) to display value. */
export interface CMSPlaceholderReplacements {
  'phone-number'?: string;
  'contact-email'?: string;
  'company-address'?: string;
  'brand-name'?: string;
  'brand-tagline'?: string;
}

/** Minimal contact shape (from Settings & Branding). */
export interface ContactForPlaceholders {
  contactPhone?: string | null;
  contactEmail?: string | null;
  companyAddress?: string | null;
}

/** Minimal branding shape (from Settings & Branding). */
export interface BrandingForPlaceholders {
  brandName?: string | null;
  brandTagline?: string | null;
}

const PLACEHOLDER_REGEX = /\{\{([a-z0-9-]+)\}\}/g;

/**
 * Embed shortcodes that map to data-gsgo-embed components.
 * These are converted to embed divs before CMSBodyRenderer processes the HTML.
 */
const EMBED_SHORTCODE_MAP: Record<string, string> = {
  'blog-articles': '<div data-gsgo-embed="blog-articles"></div>',
  'blog-articles-featured': '<div data-gsgo-embed="blog-articles-featured"></div>',
};

/**
 * Replace {{key}} placeholders in text with values from the replacements map.
 * Special embed shortcodes like {{blog-articles}} are converted to embed div markers
 * so CMSBodyRenderer can render them as React components.
 * Unknown keys are replaced with empty string.
 */
export function replaceCmsPlaceholders(
  text: string,
  replacements: CMSPlaceholderReplacements
): string {
  if (!text) return text;
  return text.replace(PLACEHOLDER_REGEX, (_, key: string) => {
    // Check embed shortcodes first
    const embedDiv = EMBED_SHORTCODE_MAP[key];
    if (embedDiv) return embedDiv;
    const value = replacements[key as CMSPlaceholderKey];
    return value != null ? String(value) : '';
  });
}

/**
 * Build the replacement map from contact and branding settings (from Firestore Settings & Branding).
 */
export function getPlaceholderReplacements(
  contact?: ContactForPlaceholders | null,
  branding?: BrandingForPlaceholders | null
): CMSPlaceholderReplacements {
  return {
    'phone-number': contact?.contactPhone ?? '',
    'contact-email': contact?.contactEmail ?? '',
    'company-address': contact?.companyAddress ?? '',
    'brand-name': branding?.brandName ?? '',
    'brand-tagline': branding?.brandTagline ?? '',
  };
}
